import React, { useEffect, useRef, useState } from "react"
import { Button, Layout, Result, Spin } from "antd"
import useSearchQuery from "utils/hooks/use-search-query"
import styled from "styled-components"

const ViewerContainer = styled.div`
  width: 100%;
  height: 100vh;

  iframe,
  object {
    width: 100%;
    height: 100%;
    border: none;
  }
`

type TViewState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; blobUrl: string; contentType: string }

const isDisplayableContentType = (contentType: string): boolean =>
  contentType.includes("pdf") || contentType.includes("html")

const closeViewer = (): void => {
  window.close()
  window.location.href = "/quotes"
}

export const DocumentView: React.FC = () => {
  const searchParams = useSearchQuery()
  const documentUrl = searchParams.get("url")
  const [state, setState] = useState<TViewState>({ status: "loading" })
  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!documentUrl) {
      setState({ status: "error" })
      return
    }

    let cancelled = false

    const loadDocument = async () => {
      try {
        const response = await fetch(documentUrl)
        if (!response.ok) {
          if (!cancelled) setState({ status: "error" })
          return
        }

        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        blobUrlRef.current = blobUrl
        const contentType = response.headers.get("content-type") ?? blob.type

        if (cancelled) {
          URL.revokeObjectURL(blobUrl)
          return
        }

        if (!isDisplayableContentType(contentType)) {
          URL.revokeObjectURL(blobUrl)
          blobUrlRef.current = null
          if (!cancelled) setState({ status: "error" })
          return
        }

        setState({ status: "ready", blobUrl, contentType })
      } catch {
        window.location.replace(documentUrl)
      }
    }

    loadDocument()

    return () => {
      cancelled = true
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [documentUrl])

  if (!documentUrl || state.status === "error") {
    return (
      <Layout>
        <Result
          status="error"
          title="Unable to open document"
          subTitle="The document could not be loaded. It may still be generating, the link may have expired, or this might be an intermittent issue. Please try again in a few minutes or contact support."
          extra={
            <Button type="primary" onClick={closeViewer}>
              Close
            </Button>
          }
        />
      </Layout>
    )
  }

  if (state.status === "loading") {
    return (
      <Layout
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh"
        }}
      >
        <Spin size="large" tip="Loading document..." />
      </Layout>
    )
  }

  return (
    <ViewerContainer>
      {state.contentType.includes("pdf") ? (
        <object
          data={state.blobUrl}
          type="application/pdf"
          aria-label="Document"
        />
      ) : (
        <iframe src={state.blobUrl} title="Document" />
      )}
    </ViewerContainer>
  )
}
